import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Label } from "./ui/label";
import { X, User, UserPlus, Settings } from "lucide-react";
// Firebase imports
import { registerUser, loginUser, createAdminIfNotExists } from '../firebase/auth';
import { initializeFirebaseData } from '../firebase/initData';

const AuthModal = ({ isOpen, onClose, onLogin }) => {
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ 
    name: "", 
    email: "", 
    password: "", 
    confirmPassword: "",
    role: "client" 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInitButton, setShowInitButton] = useState(false);
  const [initLoading, setInitLoading] = useState(false);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      setError('Email et mot de passe requis');
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userData = await loginUser(loginData.email, loginData.password);
      
      // Sauvegarder les données utilisateur dans localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', 'firebase-auth'); // Token symbolique pour compatibilité
      
      onLogin(userData);
      onClose();
      
    } catch (error) {
      console.error('Erreur de connexion:', error);
      
      // Messages d'erreur Firebase en français
      let errorMessage = 'Erreur de connexion';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Aucun compte trouvé avec cet email';
        // Si c'est l'admin KILA France, proposer l'initialisation
        if (loginData.email === 'contact@kila-france.com') {
          setShowInitButton(true);
        }
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Ce compte a été désactivé';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Réessayez plus tard';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeAdmin = async () => {
    setInitLoading(true);
    setError("");
    
    try {
      console.log('🔧 Initialisation manuelle de Firebase...');
      
      // Forcer l'initialisation complète de Firebase
      await initializeFirebaseData();
      
      console.log('✅ Initialisation Firebase terminée');
      setError('Initialisation terminée ! Vous pouvez maintenant vous connecter.');
      setShowInitButton(false);
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation:', error);
      setError('Erreur lors de l\'initialisation : ' + error.message);
    } finally {
      setInitLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.password) {
      setError('Tous les champs sont requis');
      setLoading(false);
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      setLoading(false);
      return;
    }
    
    if (registerData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userData = await registerUser(registerData.email, registerData.password, {
        name: registerData.name,
        email: registerData.email,
        role: registerData.role
      });
      
      // Sauvegarder les données utilisateur
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', 'firebase-auth');
      
      onLogin(userData);
      onClose();
      
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      
      // Messages d'erreur Firebase en français
      let errorMessage = 'Erreur lors de l\'inscription';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cet email est déjà utilisé';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le mot de passe est trop faible';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Authentification</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
            {error}
          </div>
        )}

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Connexion</TabsTrigger>
            <TabsTrigger value="register">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Se connecter
                </CardTitle>
                <CardDescription>
                  Connectez-vous à votre compte CETROBON
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleLogin}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-2">
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700" 
                    disabled={loading}
                  >
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                  
                  {showInitButton && (
                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                      onClick={handleInitializeAdmin}
                      disabled={initLoading}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {initLoading ? "Initialisation..." : "Initialiser Admin KILA France"}
                    </Button>
                  )}
                </CardFooter>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Créer un compte
                </CardTitle>
                <CardDescription>
                  Rejoignez la communauté CETROBON
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleRegister}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Nom complet</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-role">Type de compte</Label>
                    <select
                      id="register-role"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={registerData.role}
                      onChange={(e) => setRegisterData({...registerData, role: e.target.value})}
                    >
                      <option value="client">Client (réserver des ateliers)</option>
                      <option value="chef">Chef/Formateur (proposer des ateliers)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirmer le mot de passe</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700" 
                    disabled={loading}
                  >
                    {loading ? "Inscription..." : "Créer mon compte"}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AuthModal;